import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { X, Loader2 } from 'lucide-react';

interface ParseProgressProps {
  percent: number;
  stage: string;
  onCancel?: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  'connecting': 'Connecting to parser',
  'authenticating': 'Authenticating',
  'uploading': 'Uploading document',
  'processing': 'Processing document',
  'extracting': 'Extracting text',
  'analyzing': 'Analyzing data',
  'complete': 'Complete',
};

export function ParseProgress({ percent, stage, onCancel }: ParseProgressProps) {
  const label = STAGE_LABELS[stage] || stage;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full mx-4">
        <div className="text-center space-y-6">
          {/* Circular progress */}
          <div className="relative w-32 h-32 mx-auto">
            <svg className="transform -rotate-90 w-32 h-32">
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                className="text-gray-200"
              />
              <circle
                cx="64"
                cy="64"
                r="56"
                stroke="currentColor"
                strokeWidth="8"
                fill="none"
                strokeDasharray={2 * Math.PI * 56}
                strokeDashoffset={2 * Math.PI * 56 * (1 - percent / 100)}
                className="text-blue-600 transition-all duration-300"
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">{Math.round(percent)}%</div>
              </div>
            </div>
          </div>
          
          {/* Stage label */}
          <div className="space-y-2">
            <div className="flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
              <p className="text-lg font-medium text-gray-900">{label}</p>
            </div>
            <p className="text-sm text-gray-500">Please wait while we process your document...</p>
          </div>
          
          {/* Progress bar */}
          <Progress value={percent} className="h-2" />
          
          {/* Cancel button */}
          {onCancel && (
            <Button
              variant="outline"
              size="sm"
              onClick={onCancel}
              className="mt-4"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
